import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UgcTab({ password }) {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("pending");
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const url = filter === "all" ? `${API}/admin/ugc` : `${API}/admin/ugc?status=${filter}`;
            const res = await axios.get(url, { headers: { "X-Admin-Password": password } });
            setItems(res.data || []);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load UGC");
        } finally {
            setLoading(false);
        }
    }, [filter, password]);

    useEffect(() => { load(); }, [load]);

    const counts = useMemo(() => ({
        pending:  items.filter((i) => i.status === "pending").length,
        approved: items.filter((i) => i.status === "approved").length,
        rejected: items.filter((i) => i.status === "rejected").length,
    }), [items]);

    const moderate = async (id, status) => {
        try {
            await axios.patch(`${API}/admin/ugc/${id}`, { status }, { headers: { "X-Admin-Password": password } });
            toast.success(status === "approved" ? "Approved · ambassador progress updated" : status === "rejected" ? "Rejected" : "Re-queued");
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Action failed");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Permanently delete this submission?")) return;
        try {
            await axios.delete(`${API}/admin/ugc/${id}`, { headers: { "X-Admin-Password": password } });
            toast.success("Deleted");
            load();
        } catch (err) {
            toast.error("Delete failed");
        }
    };

    return (
        <div data-testid="ugc-tab" className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
                {[
                    { k: "pending", label: "Pending", count: counts.pending },
                    { k: "approved", label: "Approved", count: counts.approved },
                    { k: "rejected", label: "Rejected", count: counts.rejected },
                    { k: "all", label: "All", count: items.length },
                ].map((f) => {
                    const active = filter === f.k;
                    return (
                        <button
                            key={f.k}
                            data-testid={`ugc-filter-${f.k}`}
                            onClick={() => setFilter(f.k)}
                            className={`text-[10px] font-display tracking-[0.25em] uppercase px-3 py-1.5 border ${
                                active ? "bg-[#E63946] border-[#E63946] text-white" : "border-white/10 text-zinc-400 hover:text-white"
                            }`}
                        >
                            {f.label} <span className="opacity-70">({f.count})</span>
                        </button>
                    );
                })}
            </div>

            {loading && <div className="text-zinc-500 text-sm py-12 text-center">Loading…</div>}
            {!loading && items.length === 0 && (
                <div className="bg-fgm-surface border border-white/10 p-12 text-center text-zinc-500 font-display tracking-widest uppercase">
                    No {filter !== "all" && filter} UGC submissions
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {items.map((u) => (
                        <div key={u.id} data-testid={`ugc-card-${u.id}`} className="bg-fgm-surface border border-white/10 flex flex-col">
                            {u.kind === "upload" ? (
                                <div className="aspect-square bg-black overflow-hidden">
                                    {(u.content_type || "").startsWith("video/") ? (
                                        <video
                                            src={`${process.env.REACT_APP_BACKEND_URL}${u.url}`}
                                            controls
                                            preload="metadata"
                                            className="w-full h-full object-cover"
                                        />
                                    ) : (
                                        <img
                                            src={`${process.env.REACT_APP_BACKEND_URL}${u.url}`}
                                            alt={u.filename || "ugc"}
                                            loading="lazy"
                                            className="w-full h-full object-cover"
                                        />
                                    )}
                                </div>
                            ) : (
                                <a
                                    href={u.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="aspect-square bg-black flex items-center justify-center p-4 hover:bg-zinc-950 transition-colors"
                                >
                                    <div className="text-center">
                                        <div className="text-4xl text-zinc-500 mb-2">🔗</div>
                                        <div className="text-[10px] font-mono text-zinc-400 break-all line-clamp-3">{u.url}</div>
                                        <div className="text-[10px] font-display tracking-[0.25em] uppercase text-[#E63946] mt-2">Open ↗</div>
                                    </div>
                                </a>
                            )}
                            <div className="p-3 flex-1 flex flex-col gap-2">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`text-[9px] font-display tracking-[0.25em] uppercase px-2 py-0.5 border ${
                                        u.status === "approved" ? "border-green-500/40 text-green-400" :
                                        u.status === "rejected" ? "border-zinc-500/40 text-zinc-500" :
                                        "border-yellow-500/40 text-yellow-400"
                                    }`}>
                                        ● {u.status}
                                    </span>
                                    <span className="text-[9px] font-display tracking-[0.25em] uppercase text-zinc-500">
                                        {u.kind}
                                    </span>
                                </div>
                                <div className="text-xs">
                                    <div className="text-white font-display tracking-wider truncate">{u.ambassador_name}</div>
                                    <div className="text-zinc-500 font-mono truncate">{u.ambassador_email}</div>
                                </div>
                                {u.note && (
                                    <p className="text-xs text-zinc-400 italic line-clamp-3">"{u.note}"</p>
                                )}
                                <div className="text-[10px] text-zinc-600 mt-auto">
                                    {new Date(u.created_at).toLocaleString()}
                                </div>
                                <div className="flex items-center gap-1 pt-2 border-t border-white/5">
                                    {u.status !== "approved" && (
                                        <button
                                            data-testid={`ugc-approve-${u.id}`}
                                            onClick={() => moderate(u.id, "approved")}
                                            className="flex-1 text-[10px] font-display tracking-[0.25em] uppercase px-2 py-1.5 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
                                        >
                                            ✓ Approve
                                        </button>
                                    )}
                                    {u.status !== "rejected" && (
                                        <button
                                            data-testid={`ugc-reject-${u.id}`}
                                            onClick={() => moderate(u.id, "rejected")}
                                            className="flex-1 text-[10px] font-display tracking-[0.25em] uppercase px-2 py-1.5 border border-white/15 text-zinc-400 hover:text-white"
                                        >
                                            ✗ Reject
                                        </button>
                                    )}
                                    <button
                                        data-testid={`ugc-delete-${u.id}`}
                                        onClick={() => remove(u.id)}
                                        className="text-zinc-500 hover:text-[#E63946] text-lg leading-none px-2"
                                        aria-label="Delete"
                                    >×</button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
